import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiStar, FiPhone, FiMessageCircle, FiFilter, FiSearch } from 'react-icons/fi';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { astrologers } from '../data/mockData';
import './Consultation.css';

const Consultation = () => {
  const hydrated = useHydrated();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterExpertise, setFilterExpertise] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const expertiseOptions = ['all', 'Vedic Astrology', 'Tarot Reading', 'Numerology', 'KP System', 'Nadi Astrology'];
  const statusOptions = ['all', 'online', 'busy', 'offline'];

  const filteredAstrologers = astrologers.filter(astrologer => {
    const matchesSearch = astrologer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         astrologer.expertise.some(e => e.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesExpertise = filterExpertise === 'all' || astrologer.expertise.includes(filterExpertise);
    const matchesStatus = filterStatus === 'all' || astrologer.status === filterStatus;
    return matchesSearch && matchesExpertise && matchesStatus;
  });

  return (
    <div className="consultation-page">
      <SEO
        title="Talk to Expert Astrologers - Online Consultation"
        description="Connect with verified Vedic astrologers for personalized guidance. Get expert readings on love, career, health, and marriage compatibility via chat or call."
        keywords="astrologer consultation, talk to astrologer, vedic astrology consultation, online astrologer, astrology chat"
        canonicalPath="/consult"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "AstroEpanchang Astrologer Consultation",
          "description": "Connect with verified astrologers for personalized guidance"
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What information should I prepare before an astrology consultation?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Have your exact date of birth, time of birth (from birth certificate or hospital records), and place of birth ready. The more precise your birth time, the more accurate the chart and predictions. You may also prepare a list of specific questions or life areas you want guidance on — career, relationships, health, finances, or spiritual growth."
              }
            },
            {
              "@type": "Question",
              "name": "How long does a typical astrology consultation take?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "A standard Kundli reading consultation takes 45-60 minutes. Focused sessions on specific topics (career, marriage, or muhurtham) typically take 30-45 minutes. Comprehensive life overview consultations with multiple topics can last 60-90 minutes. Chat-based consultations are available for quick questions and follow-ups."
              }
            },
            {
              "@type": "Question",
              "name": "Are online astrology consultations as accurate as in-person ones?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, the accuracy of an astrology consultation depends entirely on the astrologer's expertise and the accuracy of your birth data — not on whether it happens online or in person. Our platform uses secure video calling and chat for a seamless experience that is equally effective as a traditional in-person reading."
              }
            },
            {
              "@type": "Question",
              "name": "What languages are consultations available in?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Our astrologers offer consultations in multiple languages including Tamil, Telugu, Kannada, Malayalam, Hindi, and English. Filter astrologers by language in the search options above to find an expert who can communicate with you in your preferred language for the most comfortable and comprehensive consultation experience."
              }
            }
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1>Talk to <span className="gradient-text">Expert Astrologers</span></h1>
          <p>Connect with verified astrologers for personalized guidance on love, career, health, and more</p>
        </motion.div>

        {/* Stats Bar */}
        <div className="consultation-stats">
          <div className="stat">
            <span className="stat-value">500+</span>
            <span className="stat-label">Expert Astrologers</span>
          </div>
          <div className="stat">
            <span className="stat-value">10L+</span>
            <span className="stat-label">Consultations Done</span>
          </div>
          <div className="stat">
            <span className="stat-value">4.8★</span>
            <span className="stat-label">Average Rating</span>
          </div>
          <div className="stat">
            <span className="stat-value">24/7</span>
            <span className="stat-label">Available</span>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name or expertise..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <FiFilter className="filter-icon" />
            <select
              value={filterExpertise}
              onChange={(e) => setFilterExpertise(e.target.value)}
            >
              <option value="all">All Expertise</option>
              {expertiseOptions.slice(1).map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="busy">Busy</option>
              <option value="offline">Offline</option>
            </select>
          </div>
        </div>

        {/* Astrologers Grid */}
        <motion.div
          className="astrologers-grid"
          initial={hydrated ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {filteredAstrologers.map((astrologer) => (
            <motion.div
              key={astrologer.id}
              className="astrologer-card"
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <div className={`status-badge ${astrologer.status}`}>
                {astrologer.status}
              </div>

              <div className="astrologer-header">
                <img
                  src={astrologer.image}
                  alt={astrologer.name}
                  className="astrologer-avatar"
                />
                <div className="astrologer-info">
                  <h3>{astrologer.name}</h3>
                  <span className="astrologer-title">{astrologer.title}</span>
                  <div className="astrologer-rating">
                    <FiStar className="star" />
                    <span>{astrologer.rating}</span>
                    <span className="reviews">({astrologer.reviews} reviews)</span>
                  </div>
                </div>
              </div>

              <div className="astrologer-details">
                <div className="detail-row">
                  <span className="label">Experience:</span>
                  <span className="value">{astrologer.experience}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Languages:</span>
                  <span className="value">{astrologer.languages.join(', ')}</span>
                </div>
              </div>

              <div className="expertise-tags">
                {astrologer.expertise.slice(0, 3).map((exp, index) => (
                  <span key={index} className="expertise-tag">{exp}</span>
                ))}
              </div>

              <p className="astrologer-description">
                {astrologer.description.substring(0, 100)}...
              </p>

              <div className="price-section">
                <span className="price">
                  ${astrologer.price}<span className="per-min">/min</span>
                </span>
                <span className="consultations">
                  {astrologer.consultations.toLocaleString()} consultations
                </span>
              </div>

              <div className="action-buttons">
                <button
                  className="btn btn-primary"
                  disabled={astrologer.status === 'offline'}
                >
                  <FiPhone /> Call
                </button>
                <button
                  className="btn btn-secondary"
                  disabled={astrologer.status === 'offline'}
                >
                  <FiMessageCircle /> Chat
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {filteredAstrologers.length === 0 && (
          <div className="no-results">
            <p>No astrologers found matching your criteria. Try adjusting your filters.</p>
          </div>
        )}

        {/* Bottom SEO Content */}
        <div className="page-bottom-content">
          <h2>Online Astrology Consultation - Talk to Expert Vedic Astrologers</h2>
          <p>Get personalized astrology guidance from certified Vedic astrologers on AstroEpanchang. Our platform connects you with experienced astrology experts for birth chart readings, marriage compatibility analysis, career astrology, Muhurtham selection, and spiritual guidance. All consultations are conducted in a secure, private environment with transparent pricing.</p>
          <p>Our astrologers specialize in authentic Jyotish (Vedic astrology) traditions including Tamil astrology (Jyothidam), Kerala Jyotish, and North Indian astrology. Whether you need a detailed Kundli reading, Sade Sati analysis, Nakshatra-based career guidance, or remedial measures (Parihara), our experts provide science-backed astrological insights rooted in classical Vedic texts.</p>

          <h3>Types of Astrology Consultations Available</h3>
          <p><strong>Birth Chart (Kundli) Reading:</strong> A comprehensive analysis of your Vedic birth chart covering all 12 houses, planetary placements, yogas, current Dasha periods, and predictions for the next 1-5 years. Ideal for those seeking a complete life overview covering career, relationships, health, and finances.</p>
          <p><strong>Marriage Compatibility Consultation:</strong> Beyond the automated Porutham matching tool, get an in-depth compatibility analysis from an expert astrologer who reviews both birth charts together, identifies hidden challenges, suggests remedies for doshas, and guides on the best muhurtham timing for the wedding.</p>
          <p><strong>Career & Finance Astrology:</strong> Identify the most favorable career directions based on your 10th house lord, planetary strengths, and current Dasha period. Understand the best time cycles for career changes, business launches, investments, and financial planning based on your personal planetary calendar.</p>
          <p><strong>Muhurtham Selection:</strong> Get expert help selecting the most auspicious date and time for weddings, griha pravesh (housewarming), business inaugurations, naming ceremonies, and other important life events. Muhurtham selection considers your personal birth chart, the event type, and the Panchang of the chosen date.</p>
          <p><strong>Remedial Measures (Parihara):</strong> Receive customized Vedic remedies for planetary afflictions — including Navagraha mantras, gemstone recommendations, temple visits, charitable donations, and Homam (fire ritual) suggestions. Remedies are prescribed based on the specific challenges identified in your birth chart.</p>

          <h3>How to Choose the Right Astrologer</h3>
          <p>When selecting an astrologer for consultation, consider their area of specialization, years of experience, and the tradition they follow (Tamil, North Indian, Kerala Jyotish). Read reviews from previous clients to understand their communication style and accuracy. For marriage-related queries, look for astrologers who specialize in Vivaha (marriage) astrology. For career questions, those experienced in professional astrology and Dasha analysis will provide the most relevant guidance. All astrologers on AstroEpanchang are verified for their credentials and astrological knowledge.</p>

          <h3>Frequently Asked Questions About Astrology Consultation</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>What information should I prepare before an astrology consultation?</h4>
              <p>Have your exact date of birth, time of birth (from birth certificate or hospital records), and place of birth ready. The more precise your birth time, the more accurate the chart and predictions. You may also prepare a list of specific questions or life areas you want guidance on — career, relationships, health, finances, or spiritual growth.</p>
            </div>
            <div className="faq-seo-item">
              <h4>How long does a typical astrology consultation take?</h4>
              <p>A standard Kundli reading consultation takes 45-60 minutes. Focused sessions on specific topics (career, marriage, or muhurtham) typically take 30-45 minutes. Comprehensive life overview consultations with multiple topics can last 60-90 minutes. Chat-based consultations are available for quick questions and follow-ups.</p>
            </div>
            <div className="faq-seo-item">
              <h4>Are online astrology consultations as accurate as in-person ones?</h4>
              <p>Yes, the accuracy of an astrology consultation depends entirely on the astrologer's expertise and the accuracy of your birth data — not on whether it happens online or in person. Our platform uses secure video calling and chat for a seamless experience that is equally effective as a traditional in-person reading.</p>
            </div>
            <div className="faq-seo-item">
              <h4>What languages are consultations available in?</h4>
              <p>Our astrologers offer consultations in multiple languages including Tamil, Telugu, Kannada, Malayalam, Hindi, and English. Filter astrologers by language in the search options above to find an expert who can communicate with you in your preferred language for the most comfortable and comprehensive consultation experience.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Consultation;
